<template>
  <div v-if="modelValue" ref="popupEl" tabindex="-1" class="fixed flex flex-col bg-ui-bg shadow-lg rounded-lg border-2 overflow-hidden outline-none"
    :class="isActive ? 'border-ui-active ' : 'border-white/20 '" @keydown.esc.stop="emit('update:modelValue', false)" :style="{
      top: position.y + 'px',
      left: position.x + 'px',
      width: localWidth + 'px',
      height: popupHeight + 'px',
      minWidth: minwidth + 'px',
      minHeight: Math.min(minheight, popupHeight) + 'px',
      zIndex: zIndex,
      opacity: localOpacity
    }" @mousedown="bringToFront">
    <!-- Header (Draggable) -->
    <div class="flex flex-none text-white px-4 py-2 cursor-move justify-between" @mousedown="startDrag">
      <span class=" text-xl">{{ title }}</span>
      <div @click="$emit('update:modelValue', false)" class="cursor-pointer">
        &#10005;
      </div>
    </div>
    <div class="flex flex-1 min-h-0 flex-col">
      <!-- Content -->
      <div class="flex-1 min-h-0" :class="contentClass">
        <slot></slot>
      </div>

      <!-- Resize Edges -->
      <!-- right -->
      <div class="absolute bottom-0 left-0 top-10 w-1  cursor-ew-resize bg-transparent"
        @mousedown="startResize($event, 'ew')" />
      <!-- left -->
      <div class="absolute bottom-0 right-0 top-10 w-1  cursor-ew-resize  bg-transparent"
        @mousedown="startResize($event, 'we')" />
      <!-- bottom -->
      <div class="absolute bottom-0 left-0 right-0  h-1  cursor-ns-resize  bg-transparent"
        @mousedown="startResize($event, 'ns')" />
      <!-- top -->
      <div class="absolute top-0 left-0 right-0  h-1 cursor-ns-resize bg-transparent"
        @mousedown="startResize($event, 'sn')" />

      <!-- Resize Handles -->
      <!-- bottom right -->
      <div class="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize  bg-transparent"
        @mousedown="startResize($event, 'se')" />
      <!-- bottom left -->
      <div class="absolute bottom-0 left-0 w-4 h-4 cursor-sw-resize  bg-transparent"
        @mousedown="startResize($event, 'sw')" />
      <!-- top left -->
      <div class="absolute top-0 left-0 w-4 h-4  cursor-se-resize bg-transparent"
        @mousedown="startResize($event, 'ne')" />
      <!-- top right -->
      <div class="absolute top-0 right-0 w-4 h-4  cursor-sw-resize bg-transparent"
        @mousedown="startResize($event, 'nw')" />
    </div>
  </div>
</template>


<script setup>
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount, defineProps } from "vue";

const props = defineProps({
  id: String, // Unique ID for each popup
  position: Object, // Initial position
  title: { type: String, default: "Popup" },
  modelValue: Boolean, // Parent controls visibility
  width: {
    type: Number,
    default: 300,
  },
  height: {
    type: Number,
    default: 200,
  },
  minwidth: {
    type: Number,
    default: 200,
  },
  minheight: {
    type: Number,
    default: 100,
  },
  opacity: {
    type: Number,
    default: 90,
  },
  contentClass: {
    type: String,
    default: 'p-4 overflow-y-auto overflow-x-auto',
  },
});

const emit = defineEmits(["update:modelValue"]);

const popupEl = ref(null);

const localWidth = ref(props.width);
const localHeight = ref(props.height);
const viewportHeight = ref(window.innerHeight);
const popupHeight = computed(() => Math.min(localHeight.value, Math.max(250, viewportHeight.value - Math.max(8, position.value.y) - 8)));
const position = ref({ x: props.position?.x || 100, y: props.position?.y || 100 });
// const size = ref({ width: props.width, height: props.height });
const isDragging = ref(false);
const isResizing = ref(false);
const resizingDir = ref('se'); // New flag for resizing from left edge
const dragOffset = ref({ x: 0, y: 0 });
const snapThreshold = 20; // ⬅ Snap distance in pixels
const localOpacity = ref(props.opacity / 100);
// Track active popup globally
const isActive = ref(false);
const zIndex = ref(20);
if (!window.maxZIndex) window.maxZIndex = 20;
if (!window.activePopupId) window.activePopupId = null;

// Watch for changes in props.width and update localWidth
watch(
  () => props.width,
  (newWidth) => {
    localWidth.value = newWidth;
  }
);

// Watch for changes in props.height and update localHeight
watch(
  () => props.height,
  (newHeight) => {
    localHeight.value = newHeight;
  }
);

// When opened: raise to top, grab focus, and register for snapping.
// When closed: remove from snap registry so invisible windows don't attract others.
watch(() => props.modelValue, (newVal) => {
  if (newVal) {
    nextTick(() => {
      bringToFront();
      popupEl.value?.focus();
      window.popups[props.id] = { pos: position.value, width: localWidth.value, height: localHeight.value };
    });
  } else {
    delete window.popups[props.id];
  }
});

const bringToFront = () => {
  zIndex.value = ++window.maxZIndex;
  window.activePopupId = props.id; // Set as active popup
  isActive.value = true;
};

// Set active on click (detect globally)
const checkActiveStatus = () => {
  isActive.value = window.activePopupId === props.id;
};

// Ensure global popup tracking exists
if (!window.popups) window.popups = {};

// Register this popup when mounted only if already visible, and clean up when destroyed
onMounted(() => {
  if (props.modelValue) {
    window.popups[props.id] = { pos: position.value, width: localWidth.value, height: localHeight.value };
  }
  window.addEventListener("mousemove", onMouseMove);
  window.addEventListener("mouseup", onMouseUp);
  window.addEventListener("click", checkActiveStatus);
  window.addEventListener("resize", updateViewportSize);
});

// Unregister when component is destroyed
onBeforeUnmount(() => {
  delete window.popups[props.id];
  window.removeEventListener("mousemove", onMouseMove);
  window.removeEventListener("mouseup", onMouseUp);
  window.removeEventListener("click", checkActiveStatus);
  window.removeEventListener("resize", updateViewportSize);
});

const updateViewportSize = () => {
  viewportHeight.value = window.innerHeight;
};

const startDrag = (event) => {
  isDragging.value = true;
  dragOffset.value = {
    x: event.clientX - position.value.x,
    y: event.clientY - position.value.y,
  };
};

const startResize = (event, fromDir = false) => {
  isResizing.value = true;
  resizingDir.value = fromDir; // Mark as resizing from left edge
  event.preventDefault();
};

const onMouseMove = (event) => {
  if (isDragging.value) {
    position.value.x = event.clientX - dragOffset.value.x;
    position.value.y = event.clientY - dragOffset.value.y;
    snapToNearby(); // Apply snapping
  }
  if (isResizing.value) {
    if (resizingDir.value === 'ew') {
      const newWidth = Math.max(200, localWidth.value - (event.clientX - position.value.x));
      localWidth.value = newWidth;
      position.value.x = event.clientX; // Move position left to follow the mouse
    } else if (resizingDir.value === 'we') {
      localWidth.value = Math.max(300, event.clientX - position.value.x);
    } else if (resizingDir.value === 'se') {
      localWidth.value = Math.max(300, event.clientX - position.value.x);
      localHeight.value = Math.max(250, event.clientY - position.value.y);
    } else if (resizingDir.value === 'sw') {
      localWidth.value = Math.max(300, position.value.x + localWidth.value - event.clientX);
      localHeight.value = Math.max(250, event.clientY - position.value.y);
      position.value.x = event.clientX; // Move position left to follow the mouse
    } else if (resizingDir.value === 'ns') {
      localHeight.value = Math.max(250, event.clientY - position.value.y);
    } else if (resizingDir.value === 'sn') {
      const newHeight = Math.max(250, localHeight.value - (event.clientY - position.value.y));
      localHeight.value = newHeight;
      position.value.y = event.clientY; // Move position up to follow the mouse
    } else if (resizingDir.value === 'ne') {
      localWidth.value = Math.max(400, localWidth.value - (event.clientX - position.value.x));
      position.value.x = event.clientX; // Move position left to follow the mouse
      localHeight.value = Math.max(250, position.value.y + localHeight.value - event.clientY);
      position.value.y = event.clientY; // Move position up to follow the mouse
    } else if (resizingDir.value === 'nw') {
      localWidth.value = Math.max(300, event.clientX - position.value.x);
      // position.value.x = event.clientX; // Move position left to follow the mouse
      localHeight.value = Math.max(250, position.value.y + localHeight.value - event.clientY);
      position.value.y = event.clientY; // Move position up to follow the mouse
    }
    snapToNearby(); // Apply snapping during resizing
  }
};

const onMouseUp = () => {
  isDragging.value = false;
  isResizing.value = false;
  resizingDir.value = false; // Reset resizing from left
};

// Snapping Logic (Works for all edges and window edges)
const snapToNearby = () => {
  const windowWidth = window.innerWidth;
  const windowHeight = window.innerHeight;

  Object.entries(window.popups).forEach(([id, popup]) => {
    if (id == props.id) return; // Ignore self

    // Snap Left edge of other popup left side
    if (Math.abs(position.value.x - popup.pos.x) < snapThreshold) {
      if (isResizing.value) {
        const right_edge = position.value.x + localWidth.value;
        position.value.x = popup.pos.x; // Snap to left edge
        localWidth.value = right_edge - popup.pos.x; // Snap to left edge
      } else {
        position.value.x = popup.pos.x; // Snap to left edge
      }
    }

    // Snap right side to Right edge of other popup
    if (Math.abs((position.value.x + localWidth.value) - (popup.pos.x + popup.width)) < snapThreshold) {
      if (isResizing.value) {
        localWidth.value = popup.pos.x + popup.width - position.value.x; // Snap to right edge
      } else {
        position.value.x = popup.pos.x + popup.width - localWidth.value; // Snap to right edge
      }
    }

    // // Snap left to Right edge of other popup
    if (Math.abs(position.value.x - popup.pos.x - popup.width) < snapThreshold) {
      if (isResizing.value) {
        const right_edge = position.value.x + localWidth.value;
        position.value.x = popup.pos.x + popup.width; // Snap to right edge
        localWidth.value = right_edge - (popup.pos.x + popup.width); // Snap to right edge
      } else {
        position.value.x = popup.pos.x + popup.width; // Snap to right edge
      }
    }

    // Snap right edge to the left edge of another popup
    if (Math.abs(position.value.x + localWidth.value - popup.pos.x) < snapThreshold) {
      if (isResizing.value) {
        localWidth.value = popup.pos.x - position.value.x; // Snap to left edge
      } else {
        position.value.x = popup.pos.x - localWidth.value; // Snap left edge to the right edge of another popup
      }
    }

    // Snap top to Top edge of other popup;
    if (Math.abs(position.value.y - popup.pos.y) < snapThreshold) {
      if (isResizing.value) {
        const bottom_edge = position.value.y + localHeight.value;
        position.value.y = popup.pos.y; // Snap to top edge
        localHeight.value = bottom_edge - popup.pos.y; // Snap to top edge
      } else {
        position.value.y = popup.pos.y; // Snap to top edge
      }
    }

    // Snap bottom to Bottom edge of other popup
    if (Math.abs((position.value.y + localHeight.value) - (popup.pos.y + popup.height)) < snapThreshold) {
      if (isResizing.value) {
        localHeight.value = popup.pos.y + popup.height - position.value.y; // Snap to bottom edge
      } else {
        position.value.y = popup.pos.y + popup.height - localHeight.value; // Snap to bottom edge
      }
    }

    // Snap Bottom edge to the Top edge of another popup
    if (Math.abs(position.value.y + localHeight.value - popup.pos.y) < snapThreshold) {
      if (isResizing.value) {
        localHeight.value = popup.pos.y - position.value.y; // Snap to top edge
      } else {
        position.value.y = popup.pos.y - localHeight.value; // Snap bottom edge to the top edge of another popup
      }
    }

    // Snap Top edge to the Bottom edge of another popup
    if (Math.abs(position.value.y - popup.pos.y - popup.height) < snapThreshold) {
      if (isResizing.value) {
        const bottom_edge = position.value.y + localHeight.value;
        position.value.y = popup.pos.y + popup.height; // Snap to bottom edge
        localHeight.value = bottom_edge - (popup.pos.y + popup.height); // Snap to bottom edge
      } else {
        position.value.y = popup.pos.y + popup.height; // Snap to bottom edge
      }
    }
  });

  // Snap to left window edge
  if (Math.abs(position.value.x) < snapThreshold) {
    const right_edge = position.value.x + localWidth.value;
    position.value.x = 0;
    if (isResizing.value) {
      localWidth.value = right_edge;
    }
  }

  // Snap to right window edge
  if (Math.abs(position.value.x + localWidth.value - windowWidth) < snapThreshold) {
    if (isResizing.value) {
      localWidth.value = windowWidth - position.value.x;
    } else {
      position.value.x = windowWidth - localWidth.value;
    }
  }

  // Snap to top window edge. no need to fix the height, top can't be resized
  if (Math.abs(position.value.y) < snapThreshold) {
    if (isResizing.value) {
      localHeight.value = localHeight.value + position.value.y;
    }
    position.value.y = 0;
  }

  // Snap to bottom window edge
  if (Math.abs(position.value.y + localHeight.value - windowHeight) < snapThreshold) {
    if (isResizing.value) {
      localHeight.value = windowHeight - position.value.y;
    } else {
      position.value.y = windowHeight - localHeight.value;
    }
  }
};
</script>
